<template>
  <div
    class="flex min-h-full flex-1 flex-col justify-center py-12 sm:px-6 lg:px-8 bg-gray-50"
  >
    <!-- 返回按钮 -->
    <div class="absolute top-4 left-4 sm:top-6 sm:left-6">
      <button
        type="button"
        @click="handleBackNavigation"
        :disabled="loading || loadingDeviceUsers"
        class="flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <ArrowLeftIcon class="h-4 w-4 mr-2" aria-hidden="true" />
        {{ t("common.back") }}
      </button>
    </div>

    <!-- 头部区域，与修改密码页面一致风格 -->
    <div class="sm:mx-auto sm:w-full sm:max-w-[960px]">
      <div class="flex justify-center">
        <div class="flex items-center space-x-3">
          <ShieldCheckIcon class="h-8 w-8 text-blue-600" />
          <ArrowRightIcon class="h-5 w-5 text-gray-400" />
          <DeviceTabletIcon class="h-8 w-8 text-green-600" />
        </div>
      </div>
      <h2
        class="mt-5 text-center text-xl/8 sm:text-2xl/9 font-bold tracking-tight text-gray-900"
      >
        {{ t("security.title") }}
      </h2>
      <p class="mt-2 text-center text-sm/6 text-gray-600">
        {{ t("security.subtitle") }}
      </p>
    </div>

    <!-- 主内容区域：移动端纵向堆叠；桌面端左右分栏 -->
    <div class="mt-8 sm:mx-auto sm:w-full sm:max-w-[1100px]">
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        <!-- 左：最近登录设备（卡片） -->
        <div class="bg-white px-5 py-6 sm:px-6 sm:py-8 shadow-sm sm:rounded-lg">
          <h3 class="text-sm sm:text-base font-semibold leading-6 text-gray-900 flex items-center">
            <DevicesIcon class="h-5 w-5 text-primary-600 mr-2.5" />
            {{ t("security.recentLoginDevices") }}
          </h3>

          <div v-if="loading" class="flex justify-center items-center py-10">
            <a-spin size="large" />
          </div>

          <div v-else-if="processedUserDevices.length === 0" class="text-center py-8">
            <FolderOpenIcon class="mx-auto h-10 w-10 text-primary-500" />
            <h3 class="mt-2 text-sm font-semibold text-gray-900">
              {{ t("security.noDevices") }}
            </h3>
            <p class="mt-1 text-xs sm:text-sm text-gray-500">
              {{ t("security.noDevicesDesc") }}
            </p>
          </div>

          <div v-else class="mt-3 divide-y divide-gray-200">
            <div
              v-for="device in processedUserDevices"
              :key="device.UUID"
              class="py-3 sm:py-4 hover:bg-gray-50 transition-colors"
              :class="{ 'bg-primary-50 hover:bg-primary-100': isCurrentUserDevice(device) }"
            >
              <div class="flex items-start px-1">
                <div class="flex-shrink-0">
                  <component
                    :is="getDeviceIconComponent(device)"
                    class="h-6 w-6 text-primary-600"
                    :class="{ 'text-primary-700': isCurrentUserDevice(device) }"
                  />
                </div>
                <div class="ml-3 sm:ml-4 flex-1 min-w-0">
                  <div class="flex items-center justify-between">
                    <h4 class="text-sm font-medium text-gray-900">
                      {{ formatDeviceDisplayName(device) }}
                    </h4>
                    <a-tag :color="isCurrentUserDevice(device) ? 'blue' : 'default'" class="ml-2">
                      {{ isCurrentUserDevice(device) ? t("security.yourDevice") : t("security.otherDevice") }}
                    </a-tag>
                  </div>
                  <div class="mt-1 flex items-center text-xs sm:text-sm text-gray-500">
                    <ClockIcon class="mr-2 h-4 w-4 flex-shrink-0 text-primary-500" />
                    {{ t("security.lastLogin") }}:
                    {{ securityService.getRelativeTime(device.LastLoginTime) }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 右：当前设备用户（卡片） + 微信绑定卡片 -->
        <div class="space-y-4">
          <!-- 当前设备用户卡片 -->
          <div class="bg-white px-5 py-6 sm:px-6 sm:py-8 shadow-sm sm:rounded-lg">
            <h3 class="text-sm sm:text-base font-semibold leading-6 text-gray-900 flex items-center">
              <UsersIcon class="h-5 w-5 text-primary-600 mr-2.5" />
              {{ t("security.thisDeviceUsers") }}
            </h3>

            <div v-if="loadingDeviceUsers" class="flex justify-center items-center py-10">
              <a-spin size="large" />
            </div>

            <div v-else-if="deviceUsers.length === 0" class="text-center py-8">
              <UsersIcon class="mx-auto h-10 w-10 text-primary-500" />
              <h3 class="mt-2 text-sm font-semibold text-gray-900">
                {{ t("security.noUsers") }}
              </h3>
              <p class="mt-1 text-xs sm:text-sm text-gray-500">
                {{ t("security.noUsersDesc") }}
              </p>
            </div>

            <div v-else class="divide-y divide-gray-200">
              <div
                v-for="user in deviceUsers"
                :key="user.UserID"
                class="py-3 sm:py-4 hover:bg-gray-50 transition-colors"
                :class="{ 'bg-primary-50 hover:bg-primary-100': isCurrentUser(user) }"
              >
                <div class="flex items-start px-1">
                  <div class="flex-shrink-0">
                    <UserIcon class="h-6 w-6 text-primary-600" :class="{ 'text-primary-700': isCurrentUser(user) }" />
                  </div>
                  <div class="ml-3 sm:ml-4 flex-1 min-w-0">
                    <div class="flex items-center justify-between">
                      <h4 class="text-sm font-medium text-gray-900">
                        {{ user.userName }}
                        <span class="text-xs ml-2 text-gray-500">{{ user.UserID }}</span>
                      </h4>
                      <a-tag :color="isCurrentUser(user) ? 'blue' : 'default'" class="ml-2">
                        {{ isCurrentUser(user) ? t("security.you") : t("security.otherUser") }}
                      </a-tag>
                    </div>
                    <div class="mt-1 flex items-center text-xs sm:text-sm text-gray-500">
                      <ClockIcon class="mr-2 h-4 w-4 flex-shrink-0 text-primary-500" />
                      {{ t("security.lastLogin") }}:
                      {{ securityService.getRelativeTime(user.LastLoginTime) }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 微信绑定卡片 -->
          <WechatBindingCard
            :userId="currentUser?.username || ''"
            @unbind-success="handleWechatUnbindSuccess"
            @unbind-error="handleWechatUnbindError"
          />
        </div>
      </div>
    </div>

    <!-- 页脚说明 -->
    <div class="mt-6 sm:mx-auto sm:w-full sm:max-w-[1100px]">
      <div class="border-t pt-4 text-left text-xs text-gray-400 whitespace-pre-line">
        {{
          t("security.footerDisclaimer", {
            brandShortName: brandConfig.brandShortName,
          })
        }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { useBrandConfig } from "@/utils/brand";
import { useAuthStore } from "@/stores/auth";
import { useRouter } from "vue-router";
import { notificationService } from "@/services/notificationService";
import WechatBindingCard from "@/components/security/WechatBindingCard.vue";
import {
  DeviceTabletIcon as DevicesIcon,
  UserIcon,
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  DeviceTabletIcon,
  ServerIcon,
  FolderOpenIcon,
  ClockIcon,
  UsersIcon,
  ShieldCheckIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
} from "@heroicons/vue/24/outline";
import { securityService } from "@/services/securityService";
import { fingerprintService } from "@/services/fingerprint";
import type { DeviceUser, UserDevice } from "@/types/security";

const { t } = useI18n();
const brandConfig = useBrandConfig();
const authStore = useAuthStore();
const router = useRouter();

// 状态
const loading = ref(false);
const loadingDeviceUsers = ref(false);
const userDevices = ref<UserDevice[]>([]);
const deviceUsers = ref<DeviceUser[]>([]);

// 微信相关状态
const userWechatOpenid = ref<string>("");

// 获取当前用户信息
const currentUser = computed(() => {
  console.log("当前用户信息:", authStore.user);
  return authStore.user;
});

// 计算两周前的日期
const getTwoWeeksAgo = () => {
  const date = new Date();
  date.setDate(date.getDate() - 14);
  return date.toISOString().split("T")[0];
};

// 加载用户设备列表
const loadUserDevices = async () => {
  console.log("开始加载用户设备列表，当前用户状态:", currentUser.value);

  if (!currentUser.value?.username) {
    console.warn("用户信息未准备好，无法加载设备列表");
    return;
  }

  try {
    loading.value = true;

    // 设置查询参数，只获取过去两周的数据
    const queryParams = {
      startDate: getTwoWeeksAgo(),
      endDate: new Date().toISOString().split("T")[0],
    };

    console.log("准备加载设备列表，用户名:", currentUser.value.username);

    // 加载设备列表
    let devices = await securityService.getUserDevices(
      currentUser.value.username,
      queryParams,
    );

    // 确保devices是数组，如果为null或undefined则使用空数组
    if (!Array.isArray(devices)) {
      devices = [];
    }

    // 按最后登录时间排序，最近登录的在前
    devices = devices.sort(
      (a, b) =>
        new Date(b.LastLoginTime).getTime() -
        new Date(a.LastLoginTime).getTime(),
    );

    userDevices.value = devices;
  } catch (error) {
    console.error("加载用户设备列表失败:", error);
    notificationService.error("security.loadDevicesError");
  } finally {
    loading.value = false;
  }
};

// 加载设备用户列表
const loadDeviceUsers = async () => {
  try {
    loadingDeviceUsers.value = true;

    // 获取当前设备指纹
    await fingerprintService.getFingerprint();

    // 设置查询参数，只获取过去两周的数据
    const queryParams = {
      startDate: getTwoWeeksAgo(),
      endDate: new Date().toISOString().split("T")[0],
    };

    // 使用指纹获取设备用户列表
    let users = await securityService.getCurrentDeviceUsers(queryParams);

    // 确保users是数组，如果为null或undefined则使用空数组
    if (!Array.isArray(users)) {
      users = [];
    }

    // 按最后登录时间排序，最近登录的在前
    users = users.sort(
      (a, b) =>
        new Date(b.LastLoginTime).getTime() -
        new Date(a.LastLoginTime).getTime(),
    );

    deviceUsers.value = users;
  } catch (error) {
    console.error("加载设备用户列表失败:", error);
    notificationService.error("security.loadUsersError");
  } finally {
    loadingDeviceUsers.value = false;
  }
};

// 获取设备图标组件
const getDeviceIconComponent = (device: UserDevice) => {
  const devicePlatform = securityService.getDevicePlatform(device.Fingerprint);
  const iconType = securityService.getDeviceIconType(devicePlatform);

  switch (iconType) {
    case "mobile":
      return DevicePhoneMobileIcon;
    case "tablet":
      return DeviceTabletIcon;
    case "server":
      return ServerIcon;
    case "desktop":
    default:
      return ComputerDesktopIcon;
  }
};

// 格式化设备显示名称
const formatDeviceDisplayName = (device: UserDevice) => {
  if (!currentUser.value) return "";
  return securityService.formatDeviceDisplayName(
    device,
    currentUser.value.username || "",
    currentUser.value.name || "",
  );
};

// 判断设备是否属于当前用户
const isCurrentUserDevice = (device: UserDevice) => {
  if (!currentUser.value) return false;
  return securityService.isUserOwnDevice(
    device,
    currentUser.value.username || "",
    currentUser.value.name || "",
  );
};

// 判断是否为当前用户
const isCurrentUser = (user: DeviceUser) => {
  if (!currentUser.value) return false;
  return (
    user.UserID === currentUser.value.username ||
    user.userName === currentUser.value.name
  );
};

// 添加分组合并用户设备条目的计算属性
const processedUserDevices = computed(() => {
  const groups = new Map<string, UserDevice[]>();
  for (const device of userDevices.value) {
    let platform: string;
    try {
      platform =
        securityService.getDevicePlatform(device.Fingerprint) || "未知设备";
    } catch (error) {
      console.error("Error getting device platform:", error);
      platform = "未知设备";
    }
    const key = `${device.ownerName}|${platform}`;
    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key)!.push(device);
  }
  const consolidated: UserDevice[] = [];
  for (const devices of groups.values()) {
    const repDevice = devices.find((d) => isCurrentUserDevice(d)) || devices[0];
    const latestDevice = devices.reduce(
      (prev, curr) =>
        new Date(curr.LastLoginTime) > new Date(prev.LastLoginTime)
          ? curr
          : prev,
      devices[0],
    );
    consolidated.push({
      ...latestDevice,
      Fingerprint: repDevice.Fingerprint,
      OwnerID: repDevice.OwnerID,
      ownerName: repDevice.ownerName,
    });
  }
  return consolidated.sort((a, b) => {
    const aCurrent = isCurrentUserDevice(a);
    const bCurrent = isCurrentUserDevice(b);
    if (aCurrent && !bCurrent) return -1;
    if (!aCurrent && bCurrent) return 1;
    return (
      new Date(b.LastLoginTime).getTime() - new Date(a.LastLoginTime).getTime()
    );
  });
});

// 微信绑定事件处理
const handleWechatUnbindSuccess = () => {
  notificationService.success("security.wechat.unbindSuccess");
};

const handleWechatUnbindError = (error: any) => {
  console.error("微信解绑失败:", error);
  notificationService.error(
    error.response?.data?.message || "security.wechat.unbindError",
  );
};

// 获取用户微信信息
const getUserWechatInfo = async () => {
  try {
    // 直接从 localStorage 读取 wechat_openid
    const openid = localStorage.getItem("wechat_openid");
    userWechatOpenid.value = openid || "";

    console.log("从localStorage获取微信openid:", {
      openid: openid ? openid.substring(0, 8) + "***" : "无",
      user: currentUser.value?.username,
    });
  } catch (error) {
    console.error("获取用户微信信息失败:", error);
    userWechatOpenid.value = "";
  }
};

// 页面加载时初始化数据
onMounted(async () => {
  console.log("组件挂载，准备初始化数据");
  try {
    // 初始化设备指纹hash
    securityService.initCurrentDeviceHash();

    // 获取用户信息
    await authStore.fetchUser();
    console.log("用户信息获取完成，当前用户:", currentUser.value);

    // 获取用户微信信息
    await getUserWechatInfo();

    // 然后加载设备列表和设备用户
    await Promise.all([loadUserDevices(), loadDeviceUsers()]);
  } catch (error) {
    console.error("初始化数据失败:", error);
    notificationService.error("security.initError");
  }
});

// 返回导航，与修改密码页面一致风格
function handleBackNavigation() {
  try {
    router.push("/authorize");
  } catch (error) {
    console.error("Back navigation error:", error);
    router.push("/authorize");
  }
}
</script>
